import { useEffect, useRef, useState } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { DOMAINS_BY_SECTION, DOMAIN_LABELS, domainColor, type SatDomain, type SatMasteryBand } from '@/api/sat';
import { StopRing } from '@/components/sat/journey/StopRing';
import { SHORT_DOMAIN_LABELS } from '@/components/sat/journey/studentCopy';
import { DOMAIN_SKILL_COUNTS, FLAG } from '@/components/marketing/storyFrames';
import { usePrefersReducedMotion } from '@/hooks/usePrefersReducedMotion';
import { DIAGNOSTIC_FACTS } from '@/lib/marketingCtas';
import { tasterAnswerCount } from '@/lib/satOpenTaster';
import { cn } from '@/lib/utils';
import { LadderCard, LevelLine } from './LadderCard';
import { DOMAIN_BLURBS, LADDERS, METHOD_LINE, PROVE_IT_EXPLAINER, type LadderSubject } from './homeLadderFixture';

/**
 * `How hard does it get?` (spec 010 HOME-V3 §B): one area, four levels, four real questions,
 * in Math or in Reading & Writing. The live area's ring is the course's own ring (same
 * `StopRing`, same course order as the hero window and the course preview grid) and fills 25
 * per level answered correctly. A correct `Prove it` fills it to 100, green, with the film's flag.
 */

const GROUPS = [
  { key: 'READING_AND_WRITING', label: 'Reading & Writing', tone: 'rw', domains: DOMAINS_BY_SECTION.READING_AND_WRITING },
  { key: 'MATH', label: 'Math', tone: 'math', domains: DOMAINS_BY_SECTION.MATH },
] as const;
const SUBJECTS: readonly LadderSubject[] = ['MATH', 'RW'];
const STEP_PCT = 25;
const PROVE = 3;

/**
 * Colours on the ladder's own terms: green is earned by proving, never by volume, so a ring
 * that has climbed three rungs stays amber (the film's rose, amber, emerald in that order).
 */
const ladderBand = (pct: number): SatMasteryBand => (pct >= 100 ? 'PROFICIENT' : pct >= 50 ? 'APPROACHING' : 'DEVELOPING');

type Result = boolean | null;
const blank = (): Record<LadderSubject, Result[]> => ({ MATH: [null, null, null, null], RW: [null, null, null, null] });
const blankFlags = (): Record<LadderSubject, boolean[]> => ({ MATH: [false, false, false, false], RW: [false, false, false, false] });

export function DifficultyLadder() {
  const [subject, setSubject] = useState<LadderSubject>('MATH');
  const [active, setActive] = useState(0);
  const [results, setResults] = useState(blank);
  const [checking, setChecking] = useState(false);
  const [skillDiffers, setSkillDiffers] = useState(blankFlags);
  const [stored, setStored] = useState(0);
  const [pinned, setPinned] = useState<string | null>(null);
  const [autoFan, setAutoFan] = useState<string | null>(null);
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);

  // Hydration-only: the prerender never reads storage.
  useEffect(() => setStored(tasterAnswerCount()), []);

  const ladder = LADDERS[subject];
  const pctOf = (s: LadderSubject) => (results[s][PROVE] === true ? 100 : results[s].slice(0, PROVE).filter((r) => r === true).length * STEP_PCT);
  const livePct: Partial<Record<SatDomain, number>> = {
    [LADDERS.MATH.domain]: pctOf('MATH'),
    [LADDERS.RW.domain]: pctOf('RW'),
  };
  const proved: Partial<Record<SatDomain, boolean>> = {
    [LADDERS.MATH.domain]: results.MATH[PROVE] === true,
    [LADDERS.RW.domain]: results.RW[PROVE] === true,
  };

  // After an answer, fan the answered subject's cluster for a moment so the ring's fill is seen.
  const answeredCount = SUBJECTS.reduce((n, s) => n + results[s].filter((r) => r !== null).length, 0);
  const seenAnswers = useRef(0);
  useEffect(() => {
    if (answeredCount <= seenAnswers.current) return;
    seenAnswers.current = answeredCount;
    setAutoFan(subject === 'MATH' ? 'MATH' : 'READING_AND_WRITING');
    const t = window.setTimeout(() => setAutoFan(null), 3200);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answeredCount]);

  const select = (i: number, focus = false) => {
    if (checking) return;
    setActive(i);
    if (focus) tabRefs.current[i]?.focus();
  };
  const onKeyDown = (e: React.KeyboardEvent) => {
    const n = ladder.levels.length;
    const to =
      e.key === 'ArrowRight' ? (active + 1) % n : e.key === 'ArrowLeft' ? (active + n - 1) % n : e.key === 'Home' ? 0 : e.key === 'End' ? n - 1 : null;
    if (to == null) return;
    e.preventDefault();
    select(to, true);
  };

  const level = ladder.levels[active];
  const rowResults = results[subject];

  return (
    <section id="try" aria-labelledby="try-title" className="scroll-mt-20 pb-6 pt-12 lg:pb-10 lg:pt-14">
      <style>{LADDER_CSS}</style>
      <div className="mx-auto w-full max-w-6xl px-4 sm:px-6">
        <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,32rem)] lg:gap-14">
          <div className="contents lg:block lg:min-w-0 lg:max-w-[34rem] lg:pt-2">
            <div>
              <p data-testid="ladder-eyebrow" className="text-xs font-semibold tracking-[0.08em] text-primary">
                HOW HARD DOES IT GET?
              </p>
              <h2 id="try-title" className="mt-3 max-w-[22ch] text-balance text-2xl font-semibold leading-tight tracking-tight sm:text-3xl">
                One area, four levels. Try each one.
              </h2>
              {/* A quiet segmented choice, not a switch: which subject's ladder to climb. */}
              <div role="group" aria-label="Subject" className="mt-4 inline-flex rounded-full bg-muted/60 p-0.5 text-[13px] font-medium" data-testid="ladder-subjects">
                {SUBJECTS.map((s) => {
                  const on = s === subject;
                  const rw = s === 'RW';
                  return (
                    <button
                      key={s}
                      type="button"
                      aria-pressed={on}
                      disabled={checking}
                      data-testid={`ladder-subject-${s}`}
                      onClick={() => setSubject(s)}
                      className={cn(
                        'flex min-h-9 items-center gap-2 rounded-full px-3.5 outline-none transition-colors focus-visible:ring-2 focus-visible:ring-primary disabled:cursor-wait',
                        on ? 'bg-card text-foreground shadow-sm ring-1 ring-border' : 'text-muted-foreground hover:text-foreground',
                      )}
                    >
                      <span aria-hidden className={cn('h-2 w-2 rounded-full', rw ? 'bg-sec-rw' : 'bg-sec-math', !on && 'opacity-50')} />
                      {LADDERS[s].label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Below lg the selector sticks under the topbar while the card is read, so switching
                level never means scrolling back up. */}
            <div className="sticky top-14 z-20 -mx-4 bg-background/90 px-4 py-2 backdrop-blur supports-[backdrop-filter]:bg-background/75 sm:-mx-6 sm:px-6 lg:static lg:mx-0 lg:mt-5 lg:bg-transparent lg:p-0 lg:backdrop-blur-none">
              <div
                role="tablist"
                aria-label="Difficulty"
                aria-orientation="horizontal"
                onKeyDown={onKeyDown}
                className="grid grid-cols-4 gap-1 rounded-xl border bg-muted/50 p-1"
              >
                {ladder.levels.map((l, i) => {
                  const selected = i === active;
                  const r = rowResults[i];
                  return (
                    <button
                      key={l.key}
                      ref={(el) => {
                        tabRefs.current[i] = el;
                      }}
                      id={`ladder-tab-${l.key}`}
                      type="button"
                      role="tab"
                      aria-selected={selected}
                      aria-controls={`ladder-panel-${subject}-${l.key}`}
                      tabIndex={selected ? 0 : -1}
                      disabled={checking}
                      data-testid={`ladder-tab-${l.key}`}
                      data-result={r === null ? undefined : r ? 'correct' : 'wrong'}
                      onClick={() => select(i)}
                      className={cn(
                        'flex min-h-11 flex-col items-center justify-center gap-0.5 rounded-lg px-1 py-1.5 text-sm font-semibold outline-none transition-colors',
                        'focus-visible:ring-2 focus-visible:ring-primary disabled:cursor-wait',
                        selected ? 'bg-card text-foreground shadow-sm ring-1 ring-border' : 'text-muted-foreground hover:text-foreground',
                      )}
                    >
                      {l.label}
                      <span
                        aria-hidden
                        className={cn(
                          'h-3.5 text-[11px] leading-[14px]',
                          r === true && 'text-emerald-600 dark:text-emerald-400',
                          r === false && 'text-rose-600 dark:text-rose-400',
                          r === null && 'text-muted-foreground/60',
                        )}
                      >
                        {r === null ? '○' : r ? '✓' : '✗'}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {!skillDiffers[subject][active] && (
              <div className="mt-5 hidden lg:block" data-testid="ladder-level-line">
                <LevelLine skill={ladder.skill} level={level} className="text-[15px]" />
                {level.key === 'PROVE_IT' && (
                  <p className="mt-2 max-w-[30rem] text-pretty text-sm leading-relaxed text-muted-foreground">{PROVE_IT_EXPLAINER}</p>
                )}
              </div>
            )}
          </div>

          <div className="min-w-0">
            {SUBJECTS.map((s) =>
              LADDERS[s].levels.map((l, i) => (
                <div
                  key={`${s}-${l.key}`}
                  role="tabpanel"
                  id={`ladder-panel-${s}-${l.key}`}
                  aria-labelledby={`ladder-tab-${l.key}`}
                  hidden={s !== subject || i !== active}
                  data-testid={`ladder-panel-${s}-${l.key}`}
                >
                  <LadderCard
                    ladder={LADDERS[s]}
                    level={l}
                    next={LADDERS[s].levels[i + 1] ?? null}
                    answeredTotal={stored}
                    showLevelLine={!skillDiffers[s][i]}
                    onAnswered={(ok) => setResults((prev) => ({ ...prev, [s]: prev[s].map((r, j) => (j === i ? ok : r)) }))}
                    onSkillChange={(d) => setSkillDiffers((prev) => ({ ...prev, [s]: prev[s].map((v, j) => (j === i ? d : v)) }))}
                    onBusyChange={setChecking}
                    onNext={() => select(i + 1, true)}
                    onStored={setStored}
                  />
                </div>
              )),
            )}
          </div>
        </div>

        <div className="mt-8 border-t pt-5 lg:mt-10" data-testid="ladder-rings">
          <div className="mx-auto grid max-w-5xl gap-x-12 gap-y-1 lg:grid-cols-2 lg:justify-items-center">
            {GROUPS.map((g) => (
              <RingGroup
                key={g.key}
                group={g}
                livePct={livePct}
                proved={proved}
                pinned={pinned === g.key}
                auto={autoFan === g.key}
                onTogglePin={() => setPinned((p) => (p === g.key ? null : g.key))}
              />
            ))}
          </div>
          <p className="mx-auto mt-6 max-w-[40rem] lg:mt-7 text-pretty text-lg font-semibold leading-snug tracking-tight sm:text-xl lg:text-center" data-testid="ladder-method">
            {METHOD_LINE}
          </p>
          <p className="mx-auto mt-2 max-w-prose text-pretty text-[15px] leading-relaxed text-muted-foreground lg:text-center" data-testid="ladder-closer">
            That&apos;s one of 8 areas. The {DIAGNOSTIC_FACTS.questions}-question diagnostic maps all eight in{' '}
            {DIAGNOSTIC_FACTS.minutesLabel}.
          </p>
        </div>
      </div>
    </section>
  );
}

type Group = (typeof GROUPS)[number];

/**
 * One section of the course (Reading & Writing or Math) as one small object: its four rings
 * stacked into a cluster at rest, fanned out along a diagonal when opened, each label arriving
 * beside its ring at the end of the move. Opens on mouse hover, on keyboard focus, on tap, and
 * briefly after a ladder answer so the fill is seen. Esc closes. No box: it sits on the page.
 *
 * Layout safety: the stage has one fixed size for both states and only transforms and opacity
 * move, so opening never shifts anything (CLS 0). Reduced motion swaps the layouts instantly.
 */
function RingGroup({
  group,
  livePct,
  proved,
  pinned,
  auto,
  onTogglePin,
}: {
  group: Group;
  livePct: Partial<Record<SatDomain, number>>;
  proved: Partial<Record<SatDomain, boolean>>;
  pinned: boolean;
  auto: boolean;
  onTogglePin: () => void;
}) {
  const [hover, setHover] = useState(false);
  const [focused, setFocused] = useState(false);
  const [escaped, setEscaped] = useState(false);
  const reduced = usePrefersReducedMotion();
  const open = !escaped && (hover || focused || pinned || auto);
  const rw = group.tone === 'rw';
  const id = `ladder-group-${group.key}`;

  return (
    <section
      aria-labelledby={`${id}-title`}
      data-testid="ladder-group"
      data-section={group.key}
      data-open={open}
      onPointerEnter={(e) => {
        if (e.pointerType !== 'mouse') return;
        setEscaped(false);
        setHover(true);
      }}
      onPointerLeave={(e) => e.pointerType === 'mouse' && setHover(false)}
      onFocus={(e) => {
        if (e.target.matches(':focus-visible')) {
          if (!focused) setEscaped(false);
          setFocused(true);
        }
      }}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
          setFocused(false);
          setEscaped(false);
        }
      }}
      onKeyDown={(e) => {
        if (e.key === 'Escape' && open) {
          e.stopPropagation();
          setEscaped(true);
          if (pinned) onTogglePin();
        }
      }}
      className={cn('relative w-full rounded-xl px-1 transition-colors duration-200 lg:max-w-[34rem]', open && (rw ? 'bg-sec-rw/[0.05]' : 'bg-sec-math/[0.05]'))}
    >
      {/* Above the stage at every width (a 44px tap target). In the bottom-left corner the chevron
          collided with the fanned rings, so it no longer tucks in there on desktop. */}
      <h3 id={`${id}-title`} className="text-sm font-semibold tracking-tight lg:w-max">
        <button
          type="button"
          aria-expanded={open}
          aria-controls={`${id}-stage`}
          onClick={() => {
            setEscaped(false);
            onTogglePin();
          }}
          className="flex min-h-10 w-full items-center justify-between gap-3 rounded-lg text-left outline-none focus-visible:ring-2 focus-visible:ring-primary lg:min-h-9 lg:justify-start lg:gap-2.5 lg:pr-2"
        >
          <span className="flex items-center gap-2">
            <span aria-hidden className={cn('h-2 w-2 rounded-full', rw ? 'bg-sec-rw' : 'bg-sec-math')} />
            <span className={rw ? 'text-sec-rw' : 'text-sec-math'}>{group.label}</span>
            <span className="text-xs font-normal text-muted-foreground lg:hidden">4 areas</span>
          </span>
          <ChevronDown
            aria-hidden
            className={cn('h-3.5 w-3.5 shrink-0 text-muted-foreground/70 transition-transform duration-300 motion-reduce:transition-none', open && 'rotate-180')}
          />
        </button>
      </h3>

      <ul
        id={`${id}-stage`}
        aria-label={`${group.label} areas`}
        data-testid="ladder-stage"
        className={cn(
          'relative h-[124px] sm:h-[160px]',
          // size, scale, fan step, stack step and the cluster's resting offset: one place, per breakpoint
          '[--r:40px] [--k:.625] [--dx:40px] [--dy:28px] [--sx:8px] [--sy:6px] [--oy:33px]',
          'sm:[--dx:56px] sm:[--dy:40px] sm:[--sx:10px] sm:[--sy:7px] sm:[--oy:50px] lg:[--oy:14px]',
        )}
      >
        {group.domains.map((d, i) => {
          const pct = livePct[d];
          const live = pct !== undefined;
          const shown = pct ?? 0;
          const label = live ? `${SHORT_DOMAIN_LABELS[d]} · example` : SHORT_DOMAIN_LABELS[d];
          return (
            <li
              key={d}
              data-testid="ladder-ring"
              data-domain={d}
              className="absolute left-0 top-0 h-[var(--r)] w-[var(--r)] motion-reduce:!transition-none"
              style={{
                zIndex: open ? 10 : 40 - i * 10,
                transform: open
                  ? `translate(calc(var(--dx) * ${i}), calc(var(--dy) * ${i}))`
                  : `translate(calc(var(--sx) * ${i}), calc(var(--oy) + var(--sy) * ${i}))`,
                transition: reduced ? 'none' : `transform 520ms cubic-bezier(.3,.7,.2,1) ${open ? i * 70 : (3 - i) * 40}ms`,
              }}
            >
              <button
                type="button"
                tabIndex={open || i === 0 ? 0 : -1}
                aria-expanded={open}
                aria-label={`${DOMAIN_LABELS[d]}, ${DOMAIN_SKILL_COUNTS[d]} skills. ${DOMAIN_BLURBS[d]}`}
                onClick={() => {
                  setEscaped(false);
                  onTogglePin();
                }}
                className="relative block h-full w-full rounded-full bg-card outline-none focus-visible:ring-2 focus-visible:ring-primary"
                style={{ boxShadow: `0 0 0 1px ${domainColor(d, 0.5)}, 0 1px 3px hsl(var(--foreground) / 0.12)` }}
              >
                <span className="absolute left-0 top-0 block origin-top-left" style={{ transform: 'scale(var(--k))' }}>
                  <StopRing scorePct={shown} band={live ? ladderBand(shown) : 'NOT_STARTED'} unearned fill={{ ms: 700, delayMs: 0 }} tone={d}>
                    {null}
                  </StopRing>
                </span>
                <span className="absolute inset-0 grid place-items-center text-[11px] font-bold tracking-tight text-foreground">
                  {live && shown > 0 ? (
                    <span key={shown} className="ladder-fade-in">
                      {shown}%
                    </span>
                  ) : (
                    <span className="block h-1.5 w-1.5 rounded-full" style={{ background: domainColor(d, 0.45) }} aria-hidden />
                  )}
                </span>
                {live && proved[d] && (
                  <span
                    data-testid="story-flag"
                    role="img"
                    aria-label={`${SHORT_DOMAIN_LABELS[d]} proven`}
                    className="ladder-flag absolute -right-1.5 -top-1.5 z-10 flex h-5 w-5 items-center justify-center rounded-full bg-emerald-500 text-white shadow ring-2 ring-background"
                  >
                    <Check className="h-3 w-3" strokeWidth={3.5} aria-hidden />
                  </span>
                )}
              </button>
              <span
                aria-hidden
                className={cn(
                  'pointer-events-none absolute left-[calc(var(--r)+12px)] top-0 flex h-[var(--r)] w-44 flex-col justify-center transition-[opacity,transform] duration-300 motion-reduce:!transition-none sm:w-64',
                  open ? 'translate-x-0 opacity-100' : '-translate-x-2 opacity-0',
                )}
                style={{ transitionDelay: reduced ? '0ms' : open ? `${300 + i * 70}ms` : '0ms' }}
              >
                <span className="text-[13px] font-semibold leading-tight text-foreground">
                  {label} <span className="font-normal text-muted-foreground">· {DOMAIN_SKILL_COUNTS[d]} skills</span>
                </span>
                <span className="mt-0.5 hidden truncate text-[12px] leading-snug text-muted-foreground sm:block">{DOMAIN_BLURBS[d]}</span>
              </span>
            </li>
          );
        })}
        <li
          aria-hidden
          className={cn(
            'pointer-events-none absolute left-[calc(var(--r)+var(--sx)*3+20px)] top-[calc(var(--oy)-6px)] flex h-[calc(var(--r)+var(--sy)*3+12px)] max-w-[calc(100%-var(--r)-var(--sx)*3-20px)] flex-col justify-center transition-opacity duration-200 motion-reduce:!transition-none',
            open ? 'opacity-0' : 'opacity-100 delay-300',
          )}
          data-testid="ladder-stage-hint"
        >
          <span className="text-[13px] font-medium leading-snug text-foreground/80">
            {group.domains.map((d) => SHORT_DOMAIN_LABELS[d]).join(', ')}
          </span>
          <span className="mt-1 text-[12px] leading-snug text-muted-foreground [@media(hover:hover)]:hidden">Tap to see what each covers.</span>
          <span className="mt-1 hidden text-[12px] leading-snug text-muted-foreground [@media(hover:hover)]:block">Hover to see what each covers.</span>
        </li>
      </ul>
    </section>
  );
}

const LADDER_CSS = `
@keyframes ladder-shimmer { from { transform: translateX(-100%); } to { transform: translateX(100%); } }
@keyframes ladder-in { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
@keyframes ladder-fade { from { opacity: 0; } to { opacity: 1; } }
@keyframes ladder-flag { from { opacity: 0; transform: scale(${FLAG.fromScale}); } to { opacity: 1; transform: scale(1); } }
.ladder-shimmer::after { content: ''; position: absolute; inset: 0; background: linear-gradient(90deg, transparent, hsl(var(--primary) / 0.14), transparent); animation: ladder-shimmer 300ms ease-out both; }
.ladder-in { animation: ladder-in 300ms ease-out both; }
.ladder-fade-in { animation: ladder-fade 300ms ease-out 250ms both; }
.ladder-flag { animation: ladder-flag ${FLAG.popMs}ms ${FLAG.easing} 500ms both; }
@media (prefers-reduced-motion: reduce) { .ladder-shimmer::after, .ladder-in, .ladder-fade-in, .ladder-flag { animation: none !important; } }
`;
